import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  PlayArrow as RunIcon,
} from '@mui/icons-material';
import { Line, LineChart } from 'recharts';
import { useAuth } from '../contexts/AuthContext';
import { SectionTitle, StatCard } from '../components/performance/common';
import { ModelChecklist, PickCell, Score, modelCountText, modelLabel, timeAgo } from '../components/tests/common';
import { errorText } from '../components/tests/dialogs';
import { REPEAT_OPTIONS } from '../components/tests/RunPanel';
import { ScheduleDialog, scheduleText } from '../components/tests/ScheduleDialog';
import { RunStatus, runUrl } from '../components/tests/TargetTests';
import {
  Batch,
  DEFAULT_BATCH_REPEATS,
  Schedule,
  TargetSummary,
  TestsApi,
  fmtCredits,
  fmtPctValue,
  runIsActive,
  useTestsAccess,
} from '../services/testsApi';

const keyOf = (t: { target_type: string; target_id: string }) => `${t.target_type}:${t.target_id}`;

const targetLink = (t: TargetSummary, account: string | null) => {
  const q = account ? `&customer_id=${encodeURIComponent(account)}` : '';
  if (t.target_type === 'chain') return `/dashboard/chain-performance/${t.target_id}?tab=tests${q}`;
  return t.function_id ? `/dashboard/function-runs/${t.function_id}?tab=tests${q}` : null;
};

const Trend: React.FC<{ points: { default_accuracy: number | null }[] }> = ({ points }) =>
  points.length < 2 ? (
    <Typography variant="caption" color="text.secondary">
      –
    </Typography>
  ) : (
    <LineChart width={90} height={26} data={points}>
      <Line dataKey="default_accuracy" stroke="#2e7d32" dot={false} strokeWidth={1.5} connectNulls isAnimationActive={false} />
    </LineChart>
  );

export default function TestsOverview() {
  const { getIdToken } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [account, setAccount] = useState<string | null>(params.get('customer_id'));
  const { api, access, enabled, loading } = useTestsAccess(getIdToken, account);
  const [data, setData] = useState<{ targets: TargetSummary[]; materials: number; cases: number } | null>(null);
  const [batches, setBatches] = useState<Batch[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [batchOpen, setBatchOpen] = useState(false);
  const [scheduleEdit, setScheduleEdit] = useState<Schedule | null | undefined>(undefined);
  const [openBatch, setOpenBatch] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const refresh = useCallback(() => setReload(r => r + 1), []);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    Promise.all([api.overview(), api.batches(20), api.schedules()])
      .then(([o, b, s]) => {
        if (cancelled) return;
        setData(o);
        setBatches(b.batches);
        setSchedules(s.schedules.filter(x => x.scope === 'target'));
        setError(null);
      })
      .catch(e => !cancelled && setError(errorText(e)));
    return () => {
      cancelled = true;
    };
  }, [api, enabled, reload]);

  const busy = batches.some(b => b.status === 'running') || !!data?.targets.some(t => runIsActive(t.last_run));
  useEffect(() => {
    if (!busy) return;
    const t = setInterval(refresh, 8000);
    return () => clearInterval(t);
  }, [busy, refresh]);

  if (loading) return <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>;
  if (!enabled) return <Alert severity="info">Tests aren't turned on for this account yet.</Alert>;

  const platformId = access?.platform_account_id;
  const switchAccount = (id: string | null) => {
    setAccount(id);
    setData(null);
    setSelected(new Set());
    const next = new URLSearchParams(params);
    if (id) next.set('customer_id', id);
    else next.delete('customer_id');
    setParams(next, { replace: true });
  };

  const targets = data?.targets || [];
  const allOn = targets.length > 0 && targets.every(t => selected.has(keyOf(t)));
  const names = Object.fromEntries(targets.map(t => [keyOf(t), t.name]));

  const removeSchedule = async (s: Schedule) => {
    if (!window.confirm(`Delete the schedule "${s.name}"?`)) return;
    try {
      await api.deleteSchedule(s.id);
      refresh();
    } catch (e) {
      setError(errorText(e));
    }
  };

  const runNow = async (s: Schedule) => {
    try {
      await api.runSchedule(s.id);
      setNotice(`Started "${s.name}".`);
      refresh();
    } catch (e) {
      setError(errorText(e));
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Box>
          <Typography variant="h4">Tests</Typography>
          <Typography color="text.secondary">
            How accurate each function and chain is on its test cases, and which model fits it best. Manage inputs in the{' '}
            <Link component={RouterLink} to={`/dashboard/test-materials${account ? `?customer_id=${encodeURIComponent(account)}` : ''}`}>
              Test Library
            </Link>
            .
          </Typography>
        </Box>
        {access?.is_admin && platformId && (
          <FormControl size="small" sx={{ minWidth: 190 }}>
            <InputLabel>Account</InputLabel>
            <Select label="Account" value={account || ''} onChange={e => switchAccount(e.target.value || null)}>
              <MenuItem value="">My account</MenuItem>
              <MenuItem value={platformId}>Platform account</MenuItem>
            </Select>
          </FormControl>
        )}
      </Box>
      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
      {notice && <Alert severity="success" onClose={() => setNotice(null)} sx={{ mb: 2 }}>{notice}</Alert>}
      {!data ? (
        <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>
      ) : (
        <>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 3 }}>
            <Box sx={{ flex: 1 }}>
              <StatCard label="Functions and chains with tests" value={targets.length} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <StatCard label="Test cases" value={data.cases} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <StatCard label="Library items" value={data.materials} />
            </Box>
          </Stack>

          <SectionTitle
            title="Functions and chains"
            help="Only ones with enabled test cases are listed. Accuracy is for the current default model on the latest completed run."
            action={
              <Button variant="contained" startIcon={<RunIcon />} disabled={!selected.size} onClick={() => setBatchOpen(true)}>
                Run selected{selected.size ? ` (${selected.size})` : ''}
              </Button>
            }
          />
          {!targets.length ? (
            <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', mb: 3 }}>
              <Typography gutterBottom>Nothing has test cases yet.</Typography>
              <Typography variant="body2" color="text.secondary">
                Add cases from the Test Library, or open a function's Tests tab.
              </Typography>
            </Paper>
          ) : (
            <Paper variant="outlined" sx={{ mb: 3, overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox size="small" checked={allOn} indeterminate={!allOn && selected.size > 0} onChange={() => setSelected(allOn ? new Set() : new Set(targets.map(keyOf)))} />
                    </TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell align="right">Cases</TableCell>
                    <TableCell align="right">Accuracy</TableCell>
                    <TableCell>Trend</TableCell>
                    <TableCell align="right">Passing</TableCell>
                    <TableCell>Recommended</TableCell>
                    <TableCell>Last run</TableCell>
                    <TableCell>Schedule</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {targets.map(t => {
                    const k = keyOf(t);
                    const link = targetLink(t, account);
                    const sch = t.schedule;
                    return (
                      <TableRow key={k} hover>
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={selected.has(k)}
                            onChange={() =>
                              setSelected(s => {
                                const next = new Set(s);
                                if (next.has(k)) next.delete(k);
                                else next.add(k);
                                return next;
                              })
                            }
                          />
                        </TableCell>
                        <TableCell>
                          {link ? (
                            <Link component={RouterLink} to={link} variant="body2" sx={{ fontWeight: 600 }}>
                              {t.name}
                            </Link>
                          ) : (
                            <Typography variant="body2">{t.name}</Typography>
                          )}
                          <Typography variant="caption" color="text.secondary" component="div">
                            {t.target_type}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title={`${t.graded_cases} with an expected output`}>
                            <span>{t.cases}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">
                          <Score value={t.last_completed?.default_accuracy} />
                        </TableCell>
                        <TableCell>
                          <Trend points={t.trend} />
                        </TableCell>
                        <TableCell align="right">{fmtPctValue(t.last_completed?.default_pass_rate)}</TableCell>
                        <TableCell>{t.last_completed?.best_model ? modelLabel(t.last_completed.best_model) : '–'}</TableCell>
                        <TableCell>
                          {t.last_run ? (
                            <Box sx={{ cursor: 'pointer' }} onClick={() => navigate(runUrl(t.last_run!.id, account))}>
                              <RunStatus run={t.last_run} />
                              <Typography variant="caption" color="text.secondary">
                                {timeAgo(t.last_run.created_at)}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              never
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {sch ? (
                            <Link component="button" type="button" variant="caption" onClick={() => setScheduleEdit(sch)}>
                              {scheduleText(sch)}
                            </Link>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              –
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Paper>
          )}

          <SectionTitle title="Batches" help="Several functions or chains tested together." />
          {!batches.length ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              No batches yet. Select rows above and press Run selected.
            </Typography>
          ) : (
            <Paper variant="outlined" sx={{ mb: 3 }}>
              <Table size="small">
                <TableBody>
                  {batches.map(b => {
                    const runs = b.summary.runs || 0;
                    const done = b.summary.finished ?? b.summary.completed ?? 0;
                    return (
                      <React.Fragment key={b.id}>
                        <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => setOpenBatch(o => (o === b.id ? null : b.id))}>
                          <TableCell padding="checkbox">
                            <IconButton size="small">{openBatch === b.id ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}</IconButton>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{b.label}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {b.trigger === 'schedule' ? 'scheduled' : b.created_by || b.trigger} · {timeAgo(b.created_at)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ minWidth: 160 }}>
                            {b.status === 'running' ? (
                              <>
                                <Typography variant="caption">
                                  {done} of {runs} done
                                </Typography>
                                <LinearProgress variant="determinate" value={runs ? (100 * done) / runs : 0} />
                              </>
                            ) : (
                              <Chip size="small" color="success" label="done" />
                            )}
                          </TableCell>
                          <TableCell>
                            {b.summary.switches ? `${b.summary.switches} recommended switch${b.summary.switches === 1 ? '' : 'es'}` : ''}
                          </TableCell>
                          <TableCell align="right">{fmtCredits(b.summary.credits)} credits</TableCell>
                          <TableCell align="right" onClick={e => e.stopPropagation()}>
                            {b.status === 'running' && (
                              <Button size="small" color="warning" onClick={() => api.cancelBatch(b.id).then(refresh).catch(e => setError(errorText(e)))}>
                                Cancel
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={6} sx={{ py: 0, borderBottom: openBatch === b.id ? undefined : 'none' }}>
                            <Collapse in={openBatch === b.id} unmountOnExit>
                              <BatchTargets batch={b} api={api} account={account} />
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </Paper>
          )}

          <SectionTitle
            title="Schedules"
            help="Re-run tests on a schedule to catch regressions and try new models."
            action={
              <Button size="small" variant="outlined" onClick={() => setScheduleEdit(null)}>
                New schedule
              </Button>
            }
          />
          {!schedules.length ? (
            <Typography variant="body2" color="text.secondary">
              None yet.
            </Typography>
          ) : (
            <Paper variant="outlined">
              <Table size="small">
                <TableBody>
                  {schedules.map(s => (
                    <TableRow key={s.id}>
                      <TableCell>
                        <Typography variant="body2">{s.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {names[`${s.target_type}:${s.target_id}`] || s.target_type}
                        </Typography>
                      </TableCell>
                      <TableCell>{scheduleText(s)}</TableCell>
                      <TableCell>
                        {modelCountText(s.models)}
                        {s.auto_new_models ? ' + new' : ''} · {s.repeats}×
                      </TableCell>
                      <TableCell>{s.next_run_at ? `next ${new Date(s.next_run_at).toLocaleString()}` : ''}</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Button size="small" onClick={() => runNow(s)}>
                          Run now
                        </Button>
                        <IconButton size="small" onClick={() => setScheduleEdit(s)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => removeSchedule(s)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}
        </>
      )}
      <BatchDialog
        open={batchOpen}
        api={api}
        targets={targets.filter(t => selected.has(keyOf(t)))}
        onClose={() => setBatchOpen(false)}
        onStarted={() => {
          setBatchOpen(false);
          setSelected(new Set());
          setNotice('Batch started. Each function or chain runs in the background.');
          refresh();
        }}
      />
      <ScheduleDialog
        open={scheduleEdit !== undefined}
        api={api}
        scope="target"
        schedule={scheduleEdit || null}
        onClose={() => setScheduleEdit(undefined)}
        onSaved={() => {
          setScheduleEdit(undefined);
          refresh();
        }}
      />
    </Box>
  );
}

const BatchTargets: React.FC<{ batch: Batch; api: TestsApi | null; account: string | null }> = ({ batch: listed, api, account }) => {
  const [fresh, setFresh] = useState<Batch | null>(null);
  useEffect(() => {
    if (!api || listed.status === 'running') return;
    let cancelled = false;
    api
      .batch(listed.id)
      .then(b => !cancelled && setFresh(b))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [api, listed.id, listed.status]);
  const batch = fresh || listed;
  return (
    <Box sx={{ py: 1 }}>
      {!batch.summary.targets?.length ? (
        <Typography variant="body2" color="text.secondary">
          Nothing ran.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Accuracy</TableCell>
              <TableCell>Recommended</TableCell>
              <TableCell>High quality</TableCell>
              <TableCell>Budget</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {batch.summary.targets.map(t => (
              <TableRow key={t.run_id}>
                <TableCell>
                  <Link component={RouterLink} to={runUrl(t.run_id, account)} variant="body2">
                    {t.target_name}
                  </Link>
                </TableCell>
                <TableCell>
                  <Chip size="small" label={t.status} />
                </TableCell>
                <TableCell align="right">
                  <Score value={t.default_accuracy} />
                </TableCell>
                <TableCell>
                  <PickCell pick={t.picks?.recommended} current={t.default_model} />
                </TableCell>
                <TableCell>
                  <PickCell pick={t.picks?.quality} current={t.default_model} />
                </TableCell>
                <TableCell>
                  <PickCell pick={t.picks?.budget} current={t.default_model} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {!!batch.summary.skipped?.length && (
        <Typography variant="caption" color="text.secondary">
          Skipped: {batch.summary.skipped.map(s => s.reason).join('; ')}
        </Typography>
      )}
    </Box>
  );
};

const BatchDialog: React.FC<{
  open: boolean;
  api: TestsApi;
  targets: TargetSummary[];
  onClose: () => void;
  onStarted: () => void;
}> = ({ open, api, targets, onClose, onStarted }) => {
  const [models, setModels] = useState<string[]>(['default']);
  const [repeats, setRepeats] = useState(DEFAULT_BATCH_REPEATS);
  const [cap, setCap] = useState('');
  const [estimate, setEstimate] = useState<{ calls: number; credits: number } | null>(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const refs = targets.map(t => ({ target_type: t.target_type, target_id: t.target_id }));
  const key = JSON.stringify([refs, models, repeats]);

  useEffect(() => {
    if (open) {
      setModels(['default']);
      setRepeats(DEFAULT_BATCH_REPEATS);
      setCap('');
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !models.length) {
      setEstimate(null);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      const [r, m, n] = JSON.parse(key) as [{ target_type: 'function' | 'chain'; target_id: string }[], string[], number];
      try {
        const all = await Promise.all(r.slice(0, 25).map(x => api.estimate(x.target_type, x.target_id, { models: m, repeats: n }).catch(() => null)));
        if (cancelled) return;
        const ok = all.filter(Boolean) as { calls: number; credits: number }[];
        setEstimate({ calls: ok.reduce((s, e) => s + e.calls, 0), credits: ok.reduce((s, e) => s + e.credits, 0) });
      } catch {
        if (!cancelled) setEstimate(null);
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [api, open, key, models.length]);

  const start = async () => {
    setStarting(true);
    setError(null);
    try {
      const capValue = Number(cap);
      await api.startBatch(refs, { models, repeats }, undefined, cap && capValue > 0 ? capValue : null);
      onStarted();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setStarting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Run tests on {targets.length} function{targets.length === 1 ? '' : 's'} or chain{targets.length === 1 ? '' : 's'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Models
            </Typography>
            <ModelChecklist value={models} onChange={setModels} context={{}} api={api} />
          </Box>
          <Stack direction="row" spacing={2}>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Runs per case
              </Typography>
              <Select size="small" value={repeats} onChange={e => setRepeats(Number(e.target.value))} sx={{ minWidth: 100 }}>
                {REPEAT_OPTIONS.map(n => (
                  <MenuItem key={n} value={n}>
                    {n}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Credit cap per run (optional)
              </Typography>
              <TextField size="small" type="number" value={cap} onChange={e => setCap(e.target.value)} placeholder="Default" sx={{ width: 150 }} />
            </Box>
          </Stack>
          <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1.5 }}>
            <Typography variant="body2">
              {estimate ? (
                <>
                  <b>{estimate.calls}</b> calls · about <b>{fmtCredits(estimate.credits)}</b> credits. Billed like normal runs and labelled "Tests".
                </>
              ) : (
                'Working out the cost…'
              )}
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={start} disabled={starting || !models.length || !targets.length}>
          {starting ? 'Starting…' : 'Start'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
